import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import './SocialActivityFeed.css';
import ActivitySocialBar from './ActivitySocialBar';
import CameraVideoMedia, { CameraVideoTile } from './CameraVideoMedia';
import CameraMediaFeedStatus from './CameraMediaFeedStatus';
import { useCameraMediaFeed } from './useCameraMediaFeed';
import { isCameraMediaActivity, mergeCameraMedia } from '../../services/cameraMediaFeed';
import GracePeriodCountdown from '../GracePeriodCountdown';
import { useModal } from '../../services/ModalService';
import SocialModal from '../SocialModal';
import DojoHomeModal from '../DojoHomeModal';
import Activity from './Activity';
import SocialProfileModal from '../SocialProfileModal';
import DojoRequirementsModal from '../DojoRequirementsModal';
import { getAvatarVariantUrl } from '../../utils/avatarVariants';

/**
 * Minimal activity card for swim lane display
 */
const STATUS_EMOJIS = {
  great: '🔥', good: '😊', okay: '😐', tired: '😴',
  stressed: '😤', recovering: '🩹', sick: '🤒', injured: '🤕'
};

const MiniActivityCard = ({ activity, user, isLinked, onClick, linkedActivities }) => {
  const getTypeIcon = (type) => {
    // For status_change activities, show the mood emoji directly
    if (type === 'status_change') {
      const mood = activity.data?.status || activity.name?.replace('Feeling ', '');
      return STATUS_EMOJIS[mood] || '🌡️';
    }
    switch (type) {
      case 'workout': return '🏋️';
      case 'meal': return '🍽️';
      case 'media': return '📸';
      case 'metric': return '📊';
      case 'survey': return '📝';
      default: return '📌';
    }
  };

  const getPoints = () => {
    return activity.source?.consistency_points || 0;
  };

  const points = getPoints();
  const hasLinked = linkedActivities && linkedActivities.length > 0;

  return (
    <div 
      className={`mini-activity-card ${isLinked ? 'linked' : ''} ${hasLinked ? 'has-linked' : ''}`}
      onClick={() => onClick(activity, user)}
      title={activity.name || activity.type}
    >
      <span className="mini-activity-icon">{getTypeIcon(activity.type)}</span>
      {points > 0 && (
        <span className="mini-activity-points">+{points}</span>
      )}
      {hasLinked && (
        <span className="mini-activity-link-indicator" title={`${linkedActivities.length} others did this too`}>
          🔗
        </span>
      )}
      {activity.social && (activity.social.kudos_count > 0 || activity.social.comment_count > 0) && (
        <div className="mini-card-social">
          {activity.social.kudos_count > 0 && <span className="mini-kudos">👊{activity.social.kudos_count}</span>}
          {activity.social.comment_count > 0 && <span>💬{activity.social.comment_count}</span>}
        </div>
      )}
    </div>
  );
};

/**
 * Day header row spanning all swim lanes
 */
const DayHeader = ({ date }) => {
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="swim-lane-date-label">
      <span>{formatDate(date)}</span>
    </div>
  );
};

/**
 * User column header with avatar - clickable to view profile
 */
const UserHeader = ({ user, onClick, isSelf }) => {
  return (
    <div 
      className={`swim-lane-user-header ${onClick ? 'clickable' : ''} ${isSelf ? 'is-self' : ''}`}
      onClick={() => onClick && onClick(user)}
      title={isSelf ? 'View your profile' : (onClick ? 'View profile' : undefined)}
    >
      {user.avatar_url ? (
        <img src={getAvatarVariantUrl(user, 'sm')} alt="" className="swim-lane-avatar" />
      ) : (
        <div className="swim-lane-avatar-placeholder">
          {(user.display_name || user.email || '?')[0].toUpperCase()}
        </div>
      )}
      <span className="swim-lane-user-name">{isSelf ? 'You' : (user.display_name || user.email?.split('@')[0])}</span>
    </div>
  );
};

/**
 * Full activity detail modal - renders the real Activity component in read-only mode
 */
const ActivityDetailModal = ({ activityId, user, isSelf, close, backendService, exercises }) => {
  const { open } = useModal();
  const [fullActivity, setFullActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFullActivity = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await backendService.getSocialActivity(activityId);
        setFullActivity(data.activity);
      } catch (err) {
        console.error('Error fetching full activity:', err);
        setError('Could not load activity details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFullActivity();
  }, [activityId, backendService]);

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="activity-detail-modal-inner">
      
      <div className="activity-detail-header">
          {user.avatar_url ? (
            <img src={getAvatarVariantUrl(user, 'sm')} alt="" className={`activity-detail-avatar ${isSelf ? 'is-self' : ''}`} />
          ) : (
            <div className={`activity-detail-avatar-placeholder ${isSelf ? 'is-self' : ''}`}>
              {(user.display_name || user.email || '?')[0].toUpperCase()}
            </div>
          )}
          <div className="activity-detail-user-info">
            <span className="activity-detail-user-name">{isSelf ? 'You' : (user.display_name || user.email)}</span>
            {fullActivity && (
              <span className="activity-detail-time">
                {formatDate(fullActivity.created_at)} at {formatTime(fullActivity.completed_at || fullActivity.created_at)}
              </span>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="activity-detail-loading">
            <div className="loading-spinner" />
            <span>Loading activity...</span>
          </div>
        ) : error ? (
          <div className="activity-detail-error">
            <span>{error}</span>
          </div>
        ) : fullActivity && (
          <>
            <Activity
              activity={fullActivity}
              backendService={backendService}
              exercises={exercises}
              modalService={{ open }}
              readOnly={true}
            />
            
            <ActivitySocialBar
              activityId={fullActivity._id}
              social={fullActivity.social}
              backendService={backendService}
              activityStatus={fullActivity.status}
              commentsAlwaysExpanded={!isSelf}
            />
          </>
        )}
      </div>
  );
};

/**
 * SocialActivityFeed - Displays friend/dojo activities in vertical swim lanes
 */
const SocialActivityFeed = ({ mode, backendService, startDate, endDate, exercises }) => {
  const { open } = useModal();
  const [feedData, setFeedData] = useState({ users: [], activities: [], hasFriends: null, inDojo: null });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [dojoCompliance, setDojoCompliance] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [oldestDate, setOldestDate] = useState(null);

  const requestIdRef = useRef(0);
  // Authorized camera videos, joined only for authors in this feed's lanes.
  const cameraMedia = useCameraMediaFeed(backendService, 'feed', hasMore ? oldestDate : null);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const isLoadingMoreRef = useRef(false);
  const stickyHeaderRef = useRef(null);
  const [stickyTop, setStickyTop] = useState(0);

  const currentUserId = useMemo(() => {
    const user = backendService.getUser ? backendService.getUser() : null;
    if (!user) return null;
    return user._id || user.user_id || user.id || null;
  }, [backendService]);

  // Track top-bar height and visibility so the sticky header sits below it
  useEffect(() => {
    const topBar = document.querySelector('.top-bar');
    if (!topBar) return;

    const update = () => {
      const isHidden = topBar.classList.contains('hidden');
      setStickyTop(isHidden ? 0 : topBar.offsetHeight);
    };

    // Initial measurement
    update();

    // Re-measure on scroll (top-bar toggles visibility on scroll)
    window.addEventListener('scroll', update, { passive: true });
    // Re-measure on resize in case top-bar height changes
    window.addEventListener('resize', update, { passive: true });

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleOpenSocialModal = (initialTab) => {
    open(initialTab === 'dojo' ? DojoHomeModal : SocialModal, {
      backendService, 
      ...(initialTab === 'dojo' ? {} : { initialTab })
    });
  };

  const handleOpenRequirementsModal = () => {
    if (dojoCompliance?.dojoId) {
      open(DojoRequirementsModal, {
        backendService,
        dojoId: dojoCompliance.dojoId,
        dojoName: dojoCompliance.dojoName
      });
    }
  };

  const handleUserClick = (user) => {
    if (user?._id) {
      open(SocialProfileModal, {
        userId: user._id,
        userName: user.display_name || user.email?.split('@')[0],
        backendService
      });
    }
  };

  const fetchFeed = useCallback(async (isLoadMore = false, beforeDate = null) => {
    const fetchId = ++requestIdRef.current; // track latest request to avoid stale updates

    if (isLoadMore) {
      // Guard against concurrent load-more calls
      if (isLoadingMoreRef.current) return;
      isLoadingMoreRef.current = true;
    } else {
      // Initial load - reset everything
      setHasLoaded(false);
      setFeedData({ users: [], activities: [], hasFriends: null, inDojo: null });
      setIsLoading(true);
      setHasMore(true);
      setOldestDate(null);
    }

    try {
      const beforeDateParam = isLoadMore ? beforeDate : null;
      const data = await backendService.getSocialActivityFeed(mode, startDate, endDate, beforeDateParam, 30);
      if (fetchId !== requestIdRef.current) return; // discard stale responses

      if (isLoadMore) {
        // Append new activities and merge users, deduplicating by _id
        setFeedData(prev => {
          const existingIds = new Set(prev.activities.map(a => a._id));
          const newActivities = data.activities.filter(a => !existingIds.has(a._id));
          return {
            ...data,
            users: [...prev.users, ...data.users.filter(u => !prev.users.find(pu => pu._id === u._id))],
            activities: [...prev.activities, ...newActivities]
          };
        });
      } else {
        setFeedData(data);
      }
      
      setHasMore(data.hasMore || false);
      setOldestDate(data.oldestDate || null);
      
      // For dojo mode, also fetch compliance status (only on initial load)
      if (!isLoadMore && mode === 'dojo' && data.inDojo && data.dojoId) {
        try {
          const reqData = await backendService.getDojoRequirements(data.dojoId);
          if (reqData && reqData.compliance && !reqData.compliance.compliant) {
            setDojoCompliance({
              ...reqData.compliance,
              gracePeriodDays: reqData.requirements?.gracePeriodDays || 7,
              dojoName: data.dojoName,
              dojoId: data.dojoId
            });
          } else {
            setDojoCompliance(null);
          }
        } catch (err) {
          // Ignore - requirements might not be set
        }
      }
    } catch (error) {
      if (fetchId === requestIdRef.current) {
        console.error('Error fetching social feed:', error);
        // On load-more error, stop pagination to prevent stuck spinner
        if (isLoadMore) {
          setHasMore(false);
        }
      }
    } finally {
      if (fetchId === requestIdRef.current) {
        setIsLoading(false);
        setHasLoaded(true);
      }
      if (isLoadMore) {
        isLoadingMoreRef.current = false;
      }
    }
  }, [backendService, mode, startDate, endDate]);

  useEffect(() => {
    fetchFeed(false);
  }, [fetchFeed]);

  const fetchMoreActivities = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchFeed(true, oldestDate);
    }
  }, [fetchFeed, oldestDate, isLoading, hasMore]);

  // Delay showing loading spinner by 500ms to avoid flash on fast loads
  useEffect(() => {
    let timer;
    if (isLoading) {
      setShowLoadingSpinner(false); // reset immediately on new load
      timer = setTimeout(() => setShowLoadingSpinner(true), 500);
    } else {
      setShowLoadingSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isLoading]);

  // Group activities by date and user
  const groupedData = useMemo(() => {
    const { users } = feedData;
    const activities = mergeCameraMedia(feedData.activities, cameraMedia.items, {
      boundary: hasMore ? oldestDate : null, startDate, endDate, authorIds: new Set(users.map(u => String(u._id))),
    });
    if (!users.length || !activities.length) return { dates: [], userMap: {} };

    // Create user map
    const userMap = {};
    users.forEach(u => { userMap[u._id] = u; });

    // Type priority matching the "You" tab ordering
    // (workout=1, meal=2, media=3, metric=4, survey=5, status_change=6)
    const typeOrder = { workout: 1, meal: 2, media: 3, metric: 4, survey: 5, status_change: 6 };

    // Group activities by date
    const dateGroups = {};
    activities.forEach(activity => {
      const dateKey = new Date(activity.created_at).toISOString().split('T')[0];
      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = {};
      }
      const userId = activity.user_id.toString();
      if (!dateGroups[dateKey][userId]) {
        dateGroups[dateKey][userId] = [];
      }
      dateGroups[dateKey][userId].push(activity);
    });

    // Sort each user's activities within a day: newest first, then by type priority
    Object.values(dateGroups).forEach(userMap => {
      Object.keys(userMap).forEach(uid => {
        userMap[uid].sort((a, b) => {
          const timeDiff = new Date(b.created_at) - new Date(a.created_at);
          if (timeDiff !== 0) return timeDiff;
          return (typeOrder[a.type] || 99) - (typeOrder[b.type] || 99);
        });
      });
    });

    // Sort dates descending
    const sortedDates = Object.keys(dateGroups).sort((a, b) => b.localeCompare(a));

    return {
      dates: sortedDates.map(date => ({
        date,
        userActivities: dateGroups[date]
      })),
      userMap,
      users
    };
  }, [feedData, cameraMedia.items, hasMore, oldestDate, startDate, endDate]);

  // Create map of linked activities for quick lookup
  const linkedMap = useMemo(() => {
    const map = {};
    feedData.activities.forEach(a => {
      if (a.linked_activity_ids && a.linked_activity_ids.length > 0) {
        map[a._id] = a.linked_activity_ids;
      }
    });
    return map;
  }, [feedData.activities]);

  const handleActivityClick = (activity, user) => {
    const isSelf = String(user._id) === String(currentUserId);
    open(ActivityDetailModal, {
      activityId: activity._id,
      user,
      isSelf,
      backendService,
      exercises
    });
  };

  const membershipKnown = mode === 'friends'
    ? typeof feedData.hasFriends === 'boolean'
    : typeof feedData.inDojo === 'boolean';

  // Show loading state; spinner fades in after 500ms
  if (isLoading || !hasLoaded || !membershipKnown) {
    return (
      <div className={`social-feed-loading ${showLoadingSpinner ? 'visible' : ''}`} aria-hidden={!showLoadingSpinner}>
        <div className="loading-spinner" />
        <span>Loading activity feed...</span>
      </div>
    );
  }

  if (!groupedData.users || groupedData.users.length === 0) {
    // Check backend flags to determine the actual state
    const hasMembership = mode === 'friends' 
      ? feedData.hasFriends === true 
      : feedData.inDojo === true;
    
    if (!hasMembership) {
      // User has no friends / is not in a dojo
      return (
        <div className="social-feed-empty">
          <p>{mode === 'friends' ? '👥 No friends yet' : '🥋 Not in a dojo'}</p>
          <p className="social-feed-empty-hint">
            {mode === 'friends' 
              ? 'Add friends to see their activity here' 
              : 'Join a dojo to train with others'}
          </p>
          <button 
            className="social-feed-empty-action"
            onClick={() => handleOpenSocialModal(mode === 'friends' ? 'search' : 'dojo')}
          >
            {mode === 'friends' ? '🔍 Find Friends' : '🥋 Find or Create a Dojo'}
          </button>
        </div>
      );
    } else {
      // User has friends / is in a dojo, but no activities
      return (
        <div className="social-feed-empty">
          <p>No recent activity</p>
          <p className="social-feed-empty-hint">
            No one has logged any activities yet
          </p>
        </div>
      );
    }
  }

  if (groupedData.dates.length === 0) {
    return (
      <div className="social-feed-empty">
        <p>No recent activity</p>
        <p className="social-feed-empty-hint">
          No one has logged any activities yet
        </p>
      </div>
    );
  }

  return (
    <div className="social-activity-feed">
      {/* Compliance warning banner for dojo mode */}
      {mode === 'dojo' && dojoCompliance && !dojoCompliance.compliant && (
        <div className="dojo-compliance-banner">
          <div className="dojo-compliance-banner-icon">⚠️</div>
          <div className="dojo-compliance-banner-content">
            <strong>You are not meeting dojo requirements!</strong>
            <p>
              <GracePeriodCountdown 
                firstViolationAt={dojoCompliance.firstViolationAt}
                gracePeriodDays={dojoCompliance.gracePeriodDays || 7}
                expiredText={`Grace period expired! You may be removed from ${dojoCompliance.dojoName || 'the dojo'} at any time.`}
              /> left to meet the requirements or you will be removed from {dojoCompliance.dojoName || 'the dojo'}.
            </p>
            {dojoCompliance.violations && dojoCompliance.violations.length > 0 && (
              <ul className="dojo-compliance-violations">
                {dojoCompliance.violations.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            )}
          </div>
          <button 
            className="dojo-compliance-banner-btn"
            onClick={handleOpenRequirementsModal}
          >
            View Requirements
          </button>
        </div>
      )}
      
      {/* User headers row */}
      <div className="swim-lanes-header" ref={stickyHeaderRef} style={{ top: stickyTop }}>
        <div className="swim-lane-date-spacer" />
        {groupedData.users.map(user => {
          const headerIsSelf = String(user._id) === String(currentUserId);
          return (
            <div key={user._id} className={`swim-lane-header-col ${headerIsSelf ? 'is-self' : ''}`}>
              <UserHeader user={user} onClick={handleUserClick} isSelf={headerIsSelf} />
            </div>
          );
        })}
      </div>

      {/* Activity rows grouped by date */}
      <InfiniteScroll
        dataLength={feedData.activities.length}
        next={fetchMoreActivities}
        hasMore={hasMore}
        style={{ overflow: 'visible' }}
        loader={
          <div className="swim-lane-loading">
            <div className="activity-loading-spinner" />
            <span className="activity-loading-text">Loading more...</span>
          </div>
        }
        endMessage={
          feedData.activities.length > 0 && (
            <div className="swim-lane-end">
              <span>That's all for now!</span>
            </div>
          )
        }
      >
        <div className="swim-lanes-body">
          {groupedData.dates.map(({ date, userActivities }) => (
            <div key={date} className="swim-lane-date-row">
              <div className="swim-lanes-row">
                <DayHeader date={date} />
                {groupedData.users.map(user => {
                  const activities = userActivities[user._id] || [];
                  const cellIsSelf = String(user._id) === String(currentUserId);
                  return (
                    <div key={user._id} className={`swim-lane-cell ${cellIsSelf ? 'is-self' : ''}`}>
                      {activities.map(activity => isCameraMediaActivity(activity) ? (
                        <CameraVideoTile key={activity._id} activity={activity}
                          onClick={() => setSelectedCamera(selectedCamera === activity._id ? null : activity._id)} />
                      ) : (
                        <MiniActivityCard
                          key={activity._id}
                          activity={activity}
                          user={user}
                          isLinked={linkedMap[activity._id]?.length > 0}
                          linkedActivities={linkedMap[activity._id]}
                          onClick={handleActivityClick}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
              {(() => {
                // Selected camera video: full card/player under its lane row.
                const user = groupedData.users.find(u => (userActivities[u._id] || []).some(a => a._id === selectedCamera));
                const activity = user && userActivities[user._id].find(a => a._id === selectedCamera);
                return activity ? (
                  <div className="camera-video-detail">
                    <CameraVideoMedia key={activity._id} activity={activity} authorName={user.display_name || user.email?.split('@')[0]}
                      backendService={backendService} isOwner={String(user._id) === String(currentUserId)}
                      timeZone={backendService.getUser?.()?.timezone || undefined} />
                    <button type="button" onClick={() => setSelectedCamera(null)}>Close video</button>
                  </div>
                ) : null;
              })()}
            </div>
          ))}
        </div>
      </InfiniteScroll>
      <CameraMediaFeedStatus feed={cameraMedia} />

      {/* Activity detail modal */}
    </div>
  );
};

export default SocialActivityFeed;
export { ActivityDetailModal };
